import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { KcPersona } from '../ui';
import { KnowledgeService } from '../../../core/services/api/knowledge.service';
import { FeatureFlagService } from '../../../core/services/feature-flag.service';
import { KnowledgeSearchResult } from '../../../core/models/learn/knowledge-search-result';
import { KnowledgeAnswer } from '../../../core/models/learn/knowledge-answer';
import { KnowledgeSearchResultsComponent } from './knowledge-search-results.component';
import { KnowledgeAiAnswerComponent } from './knowledge-ai-answer.component';

/**
 * Blended-search OVERLAY for the Knowledge Centre hub (#47) — the modal the styled
 * `kc-search-bar` opens (matching the LOCKED variant-C prototype's `.searchmodal`):
 * a centered white card over a backdrop scrim, with a REAL autofocused text input at the
 * top, the reserved inert AI-answer slot, and the blended Learn + FAQ keyword results
 * below (delegated to KnowledgeSearchResultsComponent — type badges, breadcrumbs, members
 * lock, zero-result content-gap state).
 *
 * The hero bar itself is a readonly affordance (it doesn't take typed input); typing happens
 * HERE. Typing is debounced and calls KnowledgeService.search(query, persona); persona is the
 * reader's current intent (owned by the hub) and biases the ranking. The overlay closes on Esc,
 * backdrop click, or the X — and clicking a result routes into the reader.
 *
 * a11y: role="dialog" + aria-label; Esc closes; the input is labelled and autofocused.
 */
@Component({
  selector: 'app-knowledge-search-overlay',
  standalone: true,
  imports: [CommonModule, FormsModule, KnowledgeSearchResultsComponent, KnowledgeAiAnswerComponent],
  template: `
    <!-- backdrop scrim -->
    <div
      *ngIf="open"
      class="tw-fixed tw-inset-0 tw-z-[1100] tw-flex tw-items-start tw-justify-center tw-px-5 tw-py-16 tw-bg-[rgba(20,22,28,.5)]"
      (click)="onBackdrop($event)"
      data-testid="kc-search-overlay"
    >
      <!-- modal card -->
      <div
        class="tw-bg-white tw-rounded-[16px] tw-w-full tw-max-w-[720px] tw-shadow-[0_30px_80px_rgba(0,0,0,.34)] tw-overflow-hidden tw-flex tw-flex-col tw-max-h-[80vh]"
        role="dialog"
        aria-modal="true"
        aria-label="Search the Knowledge Centre"
        (click)="$event.stopPropagation()"
      >
        <!-- head: search icon · real input · close -->
        <div class="tw-flex tw-items-center tw-gap-3 tw-px-[18px] tw-py-4 tw-border-b tw-border-[#ececec]">
          <svg class="tw-flex-shrink-0 tw-text-[#9aa0a6]" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
          </svg>
          <label for="kc-search-input" class="tw-sr-only">Search the Knowledge Centre</label>
          <input
            #searchInput
            id="kc-search-input"
            data-testid="kc-search-input"
            type="search"
            role="searchbox"
            autocomplete="off"
            class="tw-flex-1 tw-border-none tw-outline-none tw-text-[16.5px] tw-font-[inherit] tw-bg-transparent tw-text-[#222]"
            [placeholder]="placeholder"
            [(ngModel)]="query"
            (ngModelChange)="onQueryChange($event)"
            aria-label="Search the Knowledge Centre"
          />
          <button
            type="button"
            class="tw-flex-shrink-0 tw-text-[12.5px] tw-font-semibold tw-text-[#9aa0a6] tw-bg-[#f4f5f7] tw-border-none tw-rounded-[8px] tw-px-3 tw-py-2 tw-font-[inherit] tw-cursor-pointer"
            (click)="close.emit()"
            data-testid="kc-search-close"
            aria-label="Close search"
          >
            Esc
          </button>
        </div>

        <!-- body: grounded AI answer (fills the reserved slot) + blended results (reused panel) -->
        <div class="tw-overflow-auto">
          <div *ngIf="aiEnabled && activeQuery" class="tw-max-w-[680px] tw-mx-auto tw-px-6 tw-mt-6">
            <app-knowledge-ai-answer
              [answer]="answer"
              [query]="activeQuery"
              [loading]="answerLoading"
              (feedback)="onAnswerFeedback($event)"
            ></app-knowledge-ai-answer>
          </div>
          <app-knowledge-search-results
            [results]="results"
            [query]="activeQuery"
            [loading]="loading"
          ></app-knowledge-search-results>
        </div>
      </div>
    </div>
  `,
})
export class KnowledgeSearchOverlayComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  /** Whether the overlay is shown. The hub toggles this on bar/chip click. */
  @Input() open = false;
  /** Reader intent — biases the search ranking; kept in sync with the hero persona switch. */
  @Input() persona: KcPersona = 'investor';
  /** Optional query to pre-fill + run when the overlay opens (e.g. a suggested-question chip). */
  @Input() initialQuery = '';

  @Output() close = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  query = '';
  /** The query the current results reflect (drives the empty-state message). */
  activeQuery = '';
  loading = false;
  results: KnowledgeSearchResult[] = [];

  /** The grounded AI answer (or escalate payload) for the active query; null until one is fetched. */
  answer: KnowledgeAnswer | null = null;
  answerLoading = false;
  /** The audit id of the served answer, so a 👍/👎 rating can target it. */
  private answerAuditId = 0;

  private readonly queryStream = new Subject<string>();
  private searchSub?: Subscription;

  /** Drives the grounded AI answer fetch through a switchMap so a newer query cancels an in-flight one. */
  private readonly answerStream = new Subject<string>();
  private answerSub?: Subscription;

  constructor(
    private knowledge: KnowledgeService,
    private featureFlags: FeatureFlagService,
  ) {}

  /** Whether the grounded AI answer is enabled (#50 flag). OFF → no answer is fetched or shown. */
  get aiEnabled(): boolean {
    return this.featureFlags.askAiEnabled;
  }

  ngOnInit(): void {
    // Debounce typing so each keystroke doesn't hit the API; switchMap cancels the in-flight
    // request when a newer query arrives. Persona is read at request time so it's always current.
    this.searchSub = this.queryStream
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((q) => {
          this.activeQuery = q;
          this.loading = true;
          // Fetch the grounded AI answer in parallel with the keyword results.
          this.fetchAnswer(q);
          return this.knowledge.search(q, this.persona);
        }),
      )
      .subscribe({
        next: (results) => {
          this.results = results || [];
          this.loading = false;
        },
        // search() degrades server-side; on a transport error just show the empty state.
        error: () => {
          this.results = [];
          this.loading = false;
        },
      });

    // The AI answer runs on its OWN switchMap stream: a newer query cancels the in-flight answer
    // request, so a slow response for an older query can never overwrite the current answer/auditId
    // (which would also mis-target 👍/👎 feedback). Errors degrade to "no answer" without killing
    // the stream. Persona is read at emission time, so it's always the current intent.
    this.answerSub = this.answerStream
      .pipe(
        switchMap((q) =>
          this.knowledge.answer(q, this.persona).pipe(
            map((res) => ({ answer: res.answer, auditId: res.auditId })),
            catchError(() => of({ answer: null, auditId: 0 })),
          ),
        ),
      )
      .subscribe((res) => {
        this.answer = res.answer;
        this.answerAuditId = res.auditId;
        this.answerLoading = false;
      });
  }

  /**
   * Fetches the grounded AI answer for the active query (in parallel with search). The guardrail +
   * grounding run server-side; this just renders whatever state comes back. Degrades to no answer on
   * a transport error rather than blocking the results.
   */
  private fetchAnswer(value: string): void {
    // Gated by the #50 `askAiEnabled` flag: when OFF, never fetch or show an AI answer (keyword
    // search still runs). The API also suppresses it server-side as defence-in-depth.
    if (!this.aiEnabled) {
      this.answer = null;
      this.answerLoading = false;
      return;
    }

    const q = (value || '').trim();
    if (!q) {
      this.answer = null;
      this.answerLoading = false;
      return;
    }
    this.answerLoading = true;
    this.answer = null;
    this.answerAuditId = 0;
    // Hand off to the switchMap stream — it cancels any in-flight answer for an older query.
    this.answerStream.next(q);
  }

  /** POSTs the reader's 👍/👎 rating against the served answer's audit id. Best-effort. */
  onAnswerFeedback(event: { helpful: boolean; reason?: string }): void {
    if (!this.answerAuditId) return;
    this.knowledge
      .submitAnswerFeedback(this.answerAuditId, event.helpful, event.reason)
      .subscribe({ next: () => {}, error: () => {} });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Opening (with a pre-filled query) seeds + runs the search; autofocus happens after view.
    if (changes['open'] && this.open) {
      this.query = this.initialQuery || this.query;
      if (this.query.trim()) {
        this.runSearch(this.query);
      } else {
        this.results = [];
        this.activeQuery = '';
        this.answer = null;
      }
      this.focusInput();
    }

    // Persona changed while open → re-rank the active query against the new intent.
    if (changes['persona'] && !changes['persona'].firstChange && this.open && this.activeQuery.trim()) {
      this.runSearch(this.activeQuery);
    }
  }

  ngAfterViewInit(): void {
    if (this.open) {
      this.focusInput();
    }
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.answerSub?.unsubscribe();
  }

  /** Esc closes the overlay (only when open). */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.close.emit();
    }
  }

  /** Backdrop click (outside the card) closes. */
  onBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close.emit();
    }
  }

  /** Reacts to typing in the modal's search box (debounced via the stream). */
  onQueryChange(value: string): void {
    const q = (value || '').trim();
    if (!q) {
      this.results = [];
      this.activeQuery = '';
      this.loading = false;
      this.answer = null;
      this.answerLoading = false;
      return;
    }
    this.queryStream.next(q);
  }

  /** Runs a search immediately (chip-seeded open / Enter) — bypasses the debounce. */
  /** Runs a search immediately (chip-seeded open / Enter). */
  private runSearch(value: string): void {
    const q = (value || '').trim();
    if (!q) {
      this.results = [];
      this.activeQuery = '';
      return;
    }

    // Delegate to the debounced switchMap pipeline so in-flight requests are cancelled consistently.
    // (If you truly need “no debounce” for this path, introduce a separate immediate stream and merge
    // it into the same switchMap rather than subscribing separately.)
    this.queryStream.next(q);
  }

  get placeholder(): string {
    return this.persona === 'issuer'
      ? 'Search articles, or ask a question about fundraising…'
      : 'Search articles, or ask a question about investing…';
  }

  private focusInput(): void {
    // Defer to the next tick so the input exists in the DOM (overlay just toggled open).
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }
}
